import React from 'react';
import { Link } from 'react-router-dom';
import { ButtonProps } from '../../GlobalInterface/ButtonProps';

/**
 *
 * Componente de botão da aplicação. NÃO USE A PROP ```className```, ELA VAI SOBRESCREVER AS PROPS ```extraCss``` E ```btnColor```.
 * @param btnLabel A label que ficará dentro do botão, esta String é opcional.
 * @param btnColor A cor do bootstrap que deseja aplicar ao campo. Não é necessario usar o prefixo 'btn', Ex.: 'outline-success' ou 'success'.
 * @param extraCss Uma String extra de estilização a ser passada para o ```<button />``` do componente.
 * @param children use o children para passar algo extra para dentro do componente, o children ficará antes da label do botão.
 * @param onClick A função de click do botão.
 * @param type Uma String com o tipo do botão, por padrão será utilizado o tipo ```button```,use os mesmos tipos do html, e.g.: 'submit'.
 * @param iconTag Uma String com a tag de um icon, [clique aqui para ver um exemplo](https://preview.keenthemes.com/metronic/demo7/features/icons/flaticon.html).
 * Um botao pra ser usado em toda a aplicação.
 * @param disabled Boolean que informa se o campo está desabilitado, é opcional.
 * @param disabledTitle Título do botão a ser exibido quando a propriedade `disabled` for igual a `true`
 * @param disabledCursor Cursor do mouse ao passar em cima do botão quando estiver com estado desabilitado. Valor padrão: `default`
 *
 * @author Gabriel Trindade
 **/
export const Button: React.FC<ButtonProps> = ({
  btnLabel,
  btnColor,
  extraCss,
  onClick,
  type,
  iconTag,
  children,
  disabled,
  buttonRef,
  title,
  renderAsLink = false,
  link,
  renderAsDefaultLink = false,
  dataTestId,
  disabledTitle,
  disabledCursor,
  target,
  ...rest
}) => {
  const resolvedTitle =
    disabled && disabledTitle ? disabledTitle : title ?? btnLabel;

  if (renderAsDefaultLink)
    return (
      <a
        data-testid={dataTestId ?? 'btn-render-as-html-link-id'}
        href={link}
        title={resolvedTitle}
        target={target}
        onClick={onClick as any}
        className={`btn btn-${btnColor} ${extraCss ? extraCss : ''}`}
        style={disabled ? { cursor: disabledCursor || 'default' } : undefined}
      >
        {iconTag && <i data-testid="btn-icon-test-id" className={iconTag} />}
        {children}
        {btnLabel && (
          <span className={`${(iconTag || children) && 'ml-2'}`}>
            {btnLabel}
          </span>
        )}
      </a>
    );
  if (renderAsLink)
    return (
      <Link
        data-testid={dataTestId ?? 'btn-router-link-id'}
        to={link ?? '#'}
        title={resolvedTitle}
        onClick={onClick as any}
        className={`btn btn-${btnColor} ${extraCss ? extraCss : ''}`}
        style={
        disabled
          ? {
              ...rest.style,
              cursor: disabledCursor ?? 'default',
            }
          : rest.style
      }
      >
        {iconTag && <i data-testid="btn-icon-test-id" className={iconTag} />}
        {children}
        {btnLabel && (
          <span className={`${(iconTag || children) && 'ml-2'}`}>
            {btnLabel}
          </span>
        )}
      </Link>
    );

  return (
    <button
      ref={buttonRef}
      title={resolvedTitle}
      disabled={disabled === undefined ? false : disabled}
      type={type || 'button'}
      onClick={onClick}
      data-testid={dataTestId ?? 'button-test-id'}
      className={`btn btn-${btnColor} ${extraCss ? extraCss : ''}`}
      style={
        disabled
          ? {
              ...rest.style,
              cursor: disabledCursor ?? 'default',
            }
          : rest.style
      }
      {...rest}
    >
      {iconTag && <i data-testid="btn-icon-test-id" className={iconTag} />}
      {children}
      {btnLabel && (
        <span
          data-testid="btn-label-test-id"
          className={`${(iconTag || children) && 'ml-2'}`}
        >
          {btnLabel}
        </span>
      )}
    </button>
  );
};
